import { BaseAction } from "../../../core/actions/BaseAction";
import { isShuttleOffer } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { getPassengerTypeValue, getUpperPassengerLabelByAge } from "../../../Lib/Utils/validationUtil";
import { IBookedOffer, IBooking, IBookingPassenger, IBookingTypeTrip, ISelectedPassenger } from "../../../Orchestrator/Types/Index";
import { ICalculatePassengerListByPriceActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookItAgainActionParams";
import { IGetAllTripWithoutExchangeActionParams, IGetPassengerListByOfferAdmissionsActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/FilterTripAction";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { getPassengerListByOfferAdmissionsAction } from "../CommonActions/GetPassengerListByOfferAdmissionsAction";
import { updatePaymentItemsAction } from "../CommonActions/UpdatePaymentItemsAction";

class CalculatePassengerListByPriceAction extends BaseAction<{
    outBoundPassengerPriceList: any,
    inBoundPassengerPriceList: any,
    allPassenger: IBookingPassenger
}, ICalculatePassengerListByPriceActionParams> {


    calculatePassengerListByPrice = async (booking: IBooking, screen: string = "", passengerSpecificationsTypes?: any, isSplit: boolean = false, onlyFulfillPax: boolean = false, filteredTrips?: any[], filteredBookedOffers?: any[]) => {
        let allBookedOfferWithoutExchange: IBookedOffer[]
        let allTripWithoutExchange: IBookingTypeTrip[]
        // Use filtered data if provided (for isUpgrade case), otherwise use original logic
        if ((filteredTrips && filteredTrips?.length > 0) && (filteredBookedOffers && filteredBookedOffers?.length > 0)) {
            allTripWithoutExchange = filteredTrips
            allBookedOfferWithoutExchange = filteredBookedOffers
        } else {
            await getAllOfferWithoutExchangeAction.setParams(booking.bookedOffers)
            await getAllOfferWithoutExchangeAction.execute()
            allBookedOfferWithoutExchange = isSplit ? booking.bookedOffers : getAllOfferWithoutExchangeAction.result!

            const params: IGetAllTripWithoutExchangeActionParams = {
                bookedOffers: allBookedOfferWithoutExchange,
                trips: booking.trips
            }
            await getAllTripWithoutExchangeAction.setParams(params)
            await getAllTripWithoutExchangeAction.execute()
            allTripWithoutExchange = getAllTripWithoutExchangeAction.result!
        }

        // isShuttleOffer, NOT `products[0].code === SHUTTLE_PRODUCT_CODE`. That
        // constant is the bare "SHTL" while the wire code is "SHTL_ADM", so the
        // exact comparison matched nothing and NOTHING below was ever stripped:
        // the shuttle offer stayed in the fare list and its trip — stamped
        // OUT_BOUND by the backend like every onward shuttle — sat in the
        // outbound direction beside the train. `getOffersForDirection` then
        // prices a direction from `tripsInDirection[0]` alone, so whenever the
        // gateway happened to list the BUS trip first (the order changes as
        // shuttles are added and removed) the outbound fare row became the
        // SHUTTLE'S price and the train fare left payment.total with it —
        // AED 115 charged for a AED 170 booking, measured on preprod.
        //
        // isShuttleProductCode prefix-matches and skips ANCILLARY_ITEM products,
        // which is the check every newer call site uses; this one predates it.
        const shuttleTripIds = allBookedOfferWithoutExchange
            .filter((offer: any) => isShuttleOffer(offer))
            .map((offer: any) => offer?.tripCoverage?.coveredTripId);

        const trips = allTripWithoutExchange.filter(trip => {
            return !(shuttleTripIds.includes(trip.id) && (trip.legs.length === trip.legs.filter(leg => leg.timedLeg.service.mode.ptMode === "BUS").length))
        });
        const shuttleTrips = allTripWithoutExchange.filter(trip => {
            return shuttleTripIds.includes(trip.id);
        });

        const filteredOffers = allBookedOfferWithoutExchange.filter(
            offer => offer.products.length > 0 && !isShuttleOffer(offer)
        );
        allBookedOfferWithoutExchange = filteredOffers.length === 0 ? allBookedOfferWithoutExchange : filteredOffers;

        const outBoundPassengerPriceList: any = []
        const inBoundPassengerPriceList: any = []

        const updatedPassenger: ISelectedPassenger[] = booking.passengers.map((pass) => {
            const selectedPass: ISelectedPassenger = {
                email: pass?.detail?.email || "",
                firstName: pass?.detail?.lastName || "",
                id: pass.id,
                lastName: pass?.detail?.lastName || "",
                mobile: "",
                remember: pass.remember || false,
                age: pass.age,
                seatingDetails: null,
                cards: pass.cards || [],
                prmNeeds: pass.prmNeeds || [],
            }
            return selectedPass
        })
        const shuttleTripsOrTrips = trips.length === 0 ? shuttleTrips : trips

        const admissionsParams: IGetPassengerListByOfferAdmissionsActionParams = {
            allTrip: shuttleTripsOrTrips,
            bookedOffers: allBookedOfferWithoutExchange,
            passengers: updatedPassenger
        }
        await getPassengerListByOfferAdmissionsAction.setParams(admissionsParams)
        await getPassengerListByOfferAdmissionsAction.execute()
        const allPassenger = getPassengerListByOfferAdmissionsAction.result!

        allPassenger.outbound.forEach(pass => {
            const priceDetails = {
                pId: pass.id,
                pValue: pass.passengerPrice,
                pType: getUpperPassengerLabelByAge(pass.age || 0, pass),
                pTypeValue: getPassengerTypeValue(pass),
                pStatus: pass.passengerStatus
            }
            outBoundPassengerPriceList.push(priceDetails)
        })
        allPassenger.inbound.forEach(pass => {
            const priceDetails = {
                pId: pass.id,
                pValue: pass.passengerPrice,
                pType: getUpperPassengerLabelByAge(pass.age || 0, pass),
                pTypeValue: getPassengerTypeValue(pass),
                pStatus: pass.passengerStatus
            }
            inBoundPassengerPriceList.push(priceDetails)
        })

        //PREBOOKED means this function called from processBooking function
        // const allTheFulFillmentPassengers = outBoundPassengerPriceList.filter((d: any) => d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED" || d.pStatus === "EXCHANGED")
        const allTheFulFillmentPassengers = outBoundPassengerPriceList.filter((d: any) =>
            onlyFulfillPax
              ? d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED"
              : d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED" || d.pStatus === "EXCHANGED"
          );
        const allTheRefundedPassengers = outBoundPassengerPriceList.filter((d: any) => d.pStatus === "REFUNDED" || d.pStatus === "RELEASED")

        await updatePaymentItemsAction.setParams({ atomKey: "paymentOutboundAtom", outBoundPassengerPriceList: allTheFulFillmentPassengers })
        await updatePaymentItemsAction.execute()

        await updatePaymentItemsAction.setParams({ atomKey: "paymentOutBoundRefundItemsDetails", outBoundPassengerPriceList: allTheRefundedPassengers })
        await updatePaymentItemsAction.execute()


        //PREBOOKED means this function called from processBooking function
        //const allTheFulFillmentPassengersInBound = inBoundPassengerPriceList.filter((d: any) => d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED" || d.pStatus === "EXCHANGED")
        const allTheFulFillmentPassengersInBound = inBoundPassengerPriceList.filter((d: any) =>
            onlyFulfillPax
              ? d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED"
              : d.pStatus === "FULFILLED" || d.pStatus === "PREBOOKED" || d.pStatus === "EXCHANGED"
          );
          
        const allTheRefundedPassengersInBound = inBoundPassengerPriceList.filter((d: any) => d.pStatus === "REFUNDED" || d.pStatus === "RELEASED")

        await updatePaymentItemsAction.setParams({ atomKey: "paymentInboundAtom", outBoundPassengerPriceList: allTheFulFillmentPassengersInBound })
        await updatePaymentItemsAction.execute()

        await updatePaymentItemsAction.setParams({ atomKey: "paymentOutBoundRefundItemsDetailsInBoundAtom", outBoundPassengerPriceList: allTheRefundedPassengersInBound })
        await updatePaymentItemsAction.execute()

        return { outBoundPassengerPriceList, inBoundPassengerPriceList, allPassenger }
    }

    override async run(): Promise<any> {
        let passenger: any
        try {
            const { booking, screen = "", passengerSpecificationsTypes, isSplit, onlyFulfillPax, filteredTrips, filteredBookedOffers } = this.params!
            passenger = this.calculatePassengerListByPrice(booking, screen, passengerSpecificationsTypes, isSplit, onlyFulfillPax, filteredTrips, filteredBookedOffers)
        } catch (error: any) {
            console.error("Error fetching Passenger Type:", error);
        } finally {
            return passenger
        }
    }
}

export const calculatePassengerListByPriceAction = new CalculatePassengerListByPriceAction()